import { useState, useEffect } from "react";
import PageShell from "@/components/site/PageShell";
import SEO from "@/components/site/SEO";
import { Plus, Minus } from "lucide-react";

// Preguntas y respuestas — edita este arreglo para añadir / modificar / eliminar FAQs.
const FAQ_DATA = [
  {
    category: "Reservas, Cancelaciones y Pagos",
    items: [
      {
        q: "¿Con cuánto tiempo de anticipación debo reservar y quién puede hacerlo?",
        a: "Recomendamos reservar con al menos 12 a 24 horas de anticipación para traslados urbanos/aeropuerto y de 2 a 3 días para viajes turísticos. Para solicitar y agendar cualquier servicio, la persona titular debe ser mayor de edad.",
      },
      {
        q: "¿Cuáles son las políticas de cancelación y devolución?",
        a: "Si necesitas cancelar tu servicio, debes informarnos con mínimo 4 días de antelación. En caso contrario, no se realizará devolución de dinero; sin embargo, ese monto abonado se guardará como saldo a favor para programar un nuevo servicio en el futuro.",
      },
      {
        q: "¿Puedo cambiar la hora o el lugar de recogida?",
        a: "Sí, puedes solicitar cambios en el horario o punto de recogida con al menos 1 día (24 horas) de anticipación, sujeto a disponibilidad de la flota.",
      },
      {
        q: "¿Aceptan pagos en monedas extranjeras o divisas?",
        a: "Sí, recibimos pagos en otras divisas (como USD o EUR) mediante plataformas digitales de transferencia internacional, además de transferencias locales (Bancolombia, Nequi, Daviplata), tarjetas de crédito/débito y factura electrónica.",
      },
    ],
  },
  {
    category: "Tiempos, Rutas y Vehículos",
    items: [
      {
        q: "¿Cuánto tiempo de espera incluye el servicio?",
        a: "El vehículo contará con un tiempo de espera máximo de 30 minutos en el punto fijado para el abordaje de los pasajeros. Pasado este tiempo, el servicio podría estar sujeto a recargos por tiempo adicional o reasignación según la agenda.",
      },
      {
        q: "¿Los vehículos pueden ingresar a cualquier tipo de terreno o destino?",
        a: "Te llevamos al destino que necesites; sin embargo, ten presente que no todos los vehículos están diseñados para todo tipo de terrenos (como vías destapadas o de difícil acceso). Al momento de cotizar, te recomendaremos el vehículo adecuado según el trayecto.",
      },
      {
        q: "¿Qué capacidad de pasajeros y equipaje tienen los vehículos?",
        a: "Disponemos de una flota variada que incluye camionetas, vans y microbuses para grupos pequeños, medianos y grandes (desde 5 hasta 44 pasajeros por vehículo con equipaje).",
      },
    ],
  },
  {
    category: "Políticas a Bordo, Mascotas y Equipaje",
    items: [
      {
        q: "¿Se permite el viaje con mascotas?",
        a: "¡Sí, somos pet friendly! Aceptamos mascotas siempre y cuando cumplan con las condiciones de higiene adecuadas (uso de guacal, cobija o pañal si aplica) y mantengan un buen comportamiento durante el trayecto.",
      },
      {
        q: "¿Cuáles son las normas de comportamiento dentro de los vehículos?",
        a: "Solicitamos a todos los usuarios mantener el buen uso, cuidado y respeto de las instalaciones y componentes del vehículo, así como un trato cordial con el conductor para garantizar un viaje seguro y agradable.",
      },
      {
        q: "¿Qué sucede si olvido un objeto personal en el vehículo?",
        a: "La empresa no se hace responsable por objetos, maletas o pertenencias olvidadas en los vehículos. Les recomendamos a todos los pasajeros revisar meticulosamente sus asientos y maleteros antes de descender del vehículo.",
      },
    ],
  },
  {
    category: "Servicios y Cobertura",
    items: [
      {
        q: "¿Qué tipos de transporte ofrecen?",
        a: "Ofrecemos transporte especial de pasajeros en tres modalidades: traslados urbanos en Bogotá, transfers de ida y vuelta al Aeropuerto El Dorado, y viajes turísticos o empresariales intermunicipales a nivel nacional.",
      },
      {
        q: "¿Los vehículos cuentan con los seguros reglamentarios?",
        a: "Sí, el 100% de nuestra flota cuenta con SOAT, revisión técnico-mecánica vigente y Pólizas de Responsabilidad Civil (Contractual y Extracontractual) reguladas por el Ministerio de Transporte de Colombia.",
      },
    ],
  },
];

// Build a flat index so each item has a unique key for open/close state
const FLAT = FAQ_DATA.flatMap((g, gi) =>
  g.items.map((it, ii) => ({ ...it, key: `${gi}-${ii}` }))
);

function FAQItem({ q, a, isOpen, onToggle }) {
  const panelId = `faq-panel-${q.length}-${q.charCodeAt(0)}`;
  const btnId = `faq-btn-${q.length}-${q.charCodeAt(0)}`;
  return (
    <div
      data-testid="faq-item"
      className={`rounded-md border overflow-hidden ${
        isOpen ? "bg-[#1B1712] border-[#D4AF37]" : "bg-[#15120E] border-[#D4AF37]/22"
      }`}
      style={{ transitionProperty: "background-color,border-color", transitionDuration: "220ms" }}
    >
      <button
        id={btnId}
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        data-testid="faq-question-btn"
        className="w-full flex items-center justify-between gap-4 text-left px-4 py-4 text-[0.98rem] font-medium text-[#F2EEE4] hover:text-[#E8C87A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]/60"
        style={{ transitionProperty: "color", transitionDuration: "180ms" }}
      >
        <span className="pr-2">{q}</span>
        <span className="shrink-0 text-[#D4AF37]" aria-hidden="true">
          {isOpen ? <Minus size={18} /> : <Plus size={18} />}
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={btnId}
        hidden={!isOpen}
        className="px-4 pb-4 pt-0 text-sm text-[#F5EFE0]/65 leading-relaxed"
      >
        {a}
      </div>
    </div>
  );
}

export default function FAQ() {
  const [openKey, setOpenKey] = useState(null);

  // Inject FAQPage JSON-LD (generated from the same data array) into the document head
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld";
    const payload = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FLAT.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    };
    script.text = JSON.stringify(payload);
    // Remove previous instance if page re-mounts
    document.getElementById("faq-jsonld")?.remove();
    document.head.appendChild(script);
    return () => {
      document.getElementById("faq-jsonld")?.remove();
    };
  }, []);

  return (
    <PageShell testid="page-faq">
      <SEO
        title="Preguntas Frecuentes · Líneas Doradas S.A.S."
        description="Resuelve tus dudas sobre reservas, pagos, rutas, mascotas y servicios de Líneas Doradas — transporte turístico en van, buseta y bus por toda Colombia."
        canonical="https://www.transporteenvanesybuses.com/faq"
      />

      <section className="relative pt-16 md:pt-24 pb-10">
        <div className="orb-gold w-[460px] h-[460px] right-[-120px] top-[100px] opacity-60" />
        <div className="max-w-3xl mx-auto px-5 md:px-8 relative">
          <div className="text-[#E8C87A] text-xs uppercase tracking-[0.35em] mb-4">Ayuda</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] text-[#F5EFE0]">
            <span className="italic text-gold-grad">Preguntas Frecuentes</span>
          </h1>
          <p className="text-[#F5EFE0]/70 mt-5 text-base md:text-lg leading-relaxed max-w-2xl">
            Resolvemos las dudas más comunes sobre reservas, rutas, mascotas y servicios. Si no encuentras lo que buscas,{" "}
            <a
              data-testid="faq-whatsapp-link"
              href="https://wa.me/573123616674?text=Hola%20L%C3%ADneas%20Doradas!"
              target="_blank"
              rel="noreferrer"
              className="text-[#E8C87A] hover:text-[#F5EFE0] underline underline-offset-4 decoration-[#D4AF37]/60"
              style={{ transitionProperty: "color", transitionDuration: "180ms" }}
            >
              escríbenos por WhatsApp
            </a>
            .
          </p>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-3xl mx-auto px-5 md:px-8 space-y-10">
          {FAQ_DATA.map((group, gi) => (
            <div key={group.category} data-testid={`faq-category-${gi}`}>
              <h2 className="font-display text-lg md:text-xl font-semibold text-[#E6C459] border-l-[3px] border-[#D4AF37] pl-3 mb-4">
                {group.category}
              </h2>
              <div className="space-y-2.5">
                {group.items.map((it, ii) => {
                  const key = `${gi}-${ii}`;
                  const isOpen = openKey === key;
                  return (
                    <FAQItem
                      key={key}
                      q={it.q}
                      a={it.a}
                      isOpen={isOpen}
                      onToggle={() => setOpenKey(isOpen ? null : key)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
